import type { ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';

type StateIcon = 'store' | 'live' | 'search' | 'bag' | 'chat' | 'document' | 'account';
type StateAction = { label: string; onClick?: () => void; href?: string };
type ContentStateProps = {
  kind?: 'empty' | 'error' | 'loading' | 'locked';
  icon?: StateIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: StateAction;
  secondaryAction?: StateAction;
  compact?: boolean;
  page?: boolean;
};

const symbols: Record<StateIcon, ReactNode> = {
  store: <><path d="M32 49v34h48V49M28 39l7-14h42l7 14v7c0 7-14 7-14 0 0 7-14 7-14 0 0 7-14 7-14 0 0 7-14 7-14 0v-7Z" /><path d="M47 83V62h18v21M28 39h56M43 25l-1 14m14-14v14m13-14 1 14" /></>,
  live: <><rect x="22" y="28" width="68" height="49" rx="9" /><path d="m48 41 20 12-20 12V41ZM43 88h26M56 77v11" /></>,
  search: <><circle cx="50" cy="47" r="24" /><path d="m68 66 19 19M40 47h20" /></>,
  bag: <><path d="M32 39h48l5 45H27l5-45Z" /><path d="M43 42V32a13 13 0 0 1 26 0v10M45 58c0 14 22 14 22 0" /></>,
  chat: <><path d="M34 28h44a12 12 0 0 1 12 12v26a12 12 0 0 1-12 12H49L31 89V77a12 12 0 0 1-9-11V40a12 12 0 0 1 12-12Z" /><path d="M39 46h34M39 59h22" /></>,
  document: <><path d="M33 21h33l16 16v51H33V21Z" /><path d="M66 21v18h16M45 53h24M45 65h24M45 77h14" /></>,
  account: <><circle cx="56" cy="37" r="15" /><path d="M28 86V75c0-27 56-27 56 0v11H28Z" /></>,
};

function Action({ action, secondary = false }: { action: StateAction; secondary?: boolean }) {
  return action.href
    ? <ActionButton as="a" href={action.href} $secondary={secondary}>{action.label}</ActionButton>
    : <ActionButton type="button" onClick={action.onClick} $secondary={secondary}>{action.label}</ActionButton>;
}

export default function ContentState({ kind = 'empty', icon = 'store', title, description, action, secondaryAction, compact = false, page = false }: ContentStateProps) {
  return <State $compact={compact} $page={page} role={kind === 'error' ? 'alert' : kind === 'loading' ? 'status' : undefined} aria-busy={kind === 'loading'}>
    <Illustration $compact={compact} aria-hidden="true" viewBox="0 0 112 108" fill="none">
      <circle cx="56" cy="54" r="48" fill="var(--state-soft)" />
      <g stroke="var(--state-accent)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">{symbols[icon]}</g>
      {kind === 'error' && <><circle cx="87" cy="80" r="15" fill="var(--state-surface)" stroke="var(--state-accent)" strokeWidth="2" /><path d="M87 73v8m0 6v.1" stroke="var(--state-accent)" strokeWidth="2.8" strokeLinecap="round" /></>}
      {kind === 'locked' && <><rect x="73" y="74" width="26" height="22" rx="5" fill="var(--state-surface)" stroke="var(--state-accent)" strokeWidth="2" /><path d="M80 74v-5a6 6 0 0 1 12 0v5m-6 8v6" stroke="var(--state-accent)" strokeWidth="2" strokeLinecap="round" /></>}
    </Illustration>
    <Heading as={page ? 'h1' : 'h2'} $compact={compact} $page={page}>{title}</Heading>
    {description && <Description>{description}</Description>}
    {kind === 'loading' && <LoadingTrack aria-hidden="true"><span /></LoadingTrack>}
    {(action || secondaryAction) && <Actions>
      {action && <Action action={action} />}
      {secondaryAction && <Action action={secondaryAction} secondary />}
    </Actions>}
  </State>;
}

export function StatePage({ label, backHref = '/', ...props }: ContentStateProps & { label: string; backHref?: string }) {
  return <Page>
    <PageHeader><BackLink href={backHref} aria-label="목록으로 돌아가기"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m14 5-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></BackLink><span>{label}</span></PageHeader>
    <PageBody><ContentState {...props} page /></PageBody>
    <PageFooter><span>fanbird</span><a href="/help">도움이 필요하신가요?</a></PageFooter>
  </Page>;
}

const palette = css`
  --state-surface: var(--user-surface, #fff);
  --state-ink: var(--user-text, #292433);
  --state-muted: var(--user-text-muted, #716a7c);
  --state-accent: var(--user-accent, #6941d9);
  --state-soft: var(--user-accent-soft, #f3efff);
  --state-border: var(--user-border, #e7e2ee);
  color: var(--state-ink);
  font-family: var(--font-Pretendard-Regular), sans-serif;
`;
const State = styled.section<{ $compact: boolean; $page: boolean }>`
  ${palette}
  box-sizing: border-box; width: 100%; min-width: 0;
  min-height: ${({ $compact, $page }) => $page ? 'auto' : $compact ? '200px' : '300px'};
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: ${({ $compact }) => $compact ? '24px 16px' : '40px 24px'}; text-align: center;
`;
const Illustration = styled.svg<{ $compact: boolean }>`
  display: block; flex-shrink: 0; width: ${({ $compact }) => $compact ? '72px' : '112px'};
  height: auto; margin-bottom: ${({ $compact }) => $compact ? '18px' : '28px'};
`;
const Heading = styled.h2<{ $compact: boolean; $page: boolean }>`
  max-width: 360px; margin: 0; color: var(--state-ink);
  font: inherit; font-family: var(--font-Pretendard-SemiBold), sans-serif;
  font-size: ${({ $compact, $page }) => $page ? 'clamp(22px, 5.5vw, 26px)' : $compact ? '16px' : '20px'};
  line-height: 1.45; letter-spacing: -.5px; word-break: keep-all; overflow-wrap: anywhere; text-wrap: balance;
`;
const Description = styled.p`
  max-width: 320px; margin: 12px 0 0; color: var(--state-muted);
  font-size: 14px; line-height: 1.7; word-break: keep-all; overflow-wrap: anywhere; white-space: pre-line;
`;
const Actions = styled.div`display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; width: 100%; max-width: 320px; margin-top: 26px;`;
const ActionButton = styled.button<{ $secondary: boolean }>`
  box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; flex: 1 1 125px;
  min-height: 48px; padding: 12px 18px; border: 1px solid ${({ $secondary }) => $secondary ? 'var(--state-border)' : 'transparent'};
  border-radius: 12px; background: ${({ $secondary }) => $secondary ? 'transparent' : 'var(--user-button, #6941d9)'};
  color: ${({ $secondary }) => $secondary ? 'var(--state-ink)' : '#fff'};
  font: inherit; font-family: var(--font-Pretendard-SemiBold), sans-serif; font-size: 14px;
  line-height: 1.5; text-decoration: none; cursor: pointer;
  &:focus-visible { outline: 3px solid var(--state-accent); outline-offset: 4px; }
  &:active { opacity: .8; }
`;
const progress = keyframes`from { transform: translateX(-100%); } to { transform: translateX(300%); }`;
const LoadingTrack = styled.div`
  width: 72px; height: 3px; margin-top: 26px; border-radius: 2px; overflow: hidden; background: var(--state-soft);
  span { display: block; width: 36px; height: 100%; background: var(--state-accent); animation: ${progress} 1.4s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) { span { animation: none; margin: auto; } }
`;
const Page = styled.main`
  ${palette}
  box-sizing: border-box; min-height: 100dvh; width: 100%; display: flex; flex-direction: column;
  background: var(--state-surface); padding: env(safe-area-inset-top) 0 env(safe-area-inset-bottom);
`;
const PageHeader = styled.header`
  position:sticky;top:0;z-index:10;flex-shrink:0;background:var(--state-surface);
  display: grid; grid-template-columns: 44px 1fr 44px; align-items: center; min-height: 64px;
  width: 100%; max-width: 500px; padding: 0 16px; box-sizing: border-box; margin: 0 auto;
  border-bottom: 1px solid var(--state-border); text-align: center; font-size: 16px; font-family: var(--font-Pretendard-SemiBold);
`;
const BackLink = styled.a`
  display: grid; place-items: center; width: 44px; height: 44px; color: var(--state-ink); border-radius: 8px;
  &:focus-visible { outline: 2px solid var(--state-accent); outline-offset: 2px; }
`;
const PageBody = styled.div`flex: 1; display: flex; align-items: center; width: 100%; max-width: 500px; margin: 0 auto; padding: 24px 0 64px;`;
const PageFooter = styled.footer`
  display: flex; align-items: center; justify-content: space-between; gap: 16px; width: 100%; max-width: 452px;
  padding: 20px 24px; margin: 0 auto; box-sizing: border-box; color: var(--state-muted); font-size: 12px;
  > span { font-family: var(--font-Pretendard-Bold); font-size: 18px; letter-spacing: -.7px; color: var(--state-accent); }
  a { color: inherit; min-height: 44px; display: inline-flex; align-items: center; text-underline-offset: 4px; }
  a:focus-visible { outline: 2px solid var(--state-accent); outline-offset: 3px; }
`;
